import React, { useEffect, useMemo, useState } from 'react';
import { baselineSeries, DEFAULT_ANOMALY } from '../../components/command/anomaly';
import { AnomalyTable } from '../../components/command/telemetryHud';
import { TelemetryControls } from '../../components/command/useTelemetry';
import { SignalChart } from './charts';
import { LiveSection } from './LiveFrame';

const n = (v) => (v == null || Number.isNaN(v) ? '—' : Math.round(v).toLocaleString());
const pct = (v) => (v == null || Number.isNaN(v) ? '—' : `${(v * 100).toFixed(2)}%`);

function rulesOf(raw) {
  return {
    ...DEFAULT_ANOMALY,
    ...(raw || {}),
    floors: { ...DEFAULT_ANOMALY.floors, ...(raw?.floors || {}) },
  };
}

function flagIndexes(ts, step, anomalies, metric) {
  const out = [];
  for (const a of anomalies || []) {
    if (a.metric !== metric || a.openedAt == null) continue;
    const i = ts.findIndex((t) => a.openedAt >= t && a.openedAt < t + step);
    if (i >= 0) out.push(i);
  }
  return out;
}

export default function BehaviorBaseline({ telemetry, slug = null, controls = null }) {
  const T = rulesOf(telemetry?.thresholds);
  const slugs = useMemo(() => Object.keys(telemetry?.series || {}), [telemetry]);
  const [pick, setPick] = useState(slug || slugs[0] || '');
  useEffect(() => { if (slug) setPick(slug); }, [slug]);
  const active = slug || pick || slugs[0] || '';
  const series = active ? telemetry?.series?.[active] : null;
  const step = telemetry?.window?.step || 60e3;
  const anomalies = (telemetry?.anomalies || []).filter((a) => !active || a.slug === active || a.from === active);
  const open = anomalies.filter((a) => a.closedAt == null).length;
  const hour = series && series.ts.length > 2 && (series.ts[series.ts.length - 1] - series.ts[0] >= 3 * 86400e3);

  const rate = series ? series.s5xx.map((v, i) => (series.req[i] >= T.floors.s5xxMinReq ? v / Math.max(series.req[i], 1e-9) : 0)) : [];
  const reqBase = series ? baselineSeries(series.ts, series.req, step) : [];
  const rateBase = series ? baselineSeries(series.ts, rate, step) : [];
  const p95Base = series ? baselineSeries(series.ts, series.p95, step) : [];
  const tokBase = series ? baselineSeries(series.ts, series.tokens, step) : [];

  return (
    <>
      <LiveSection title={slug ? 'How this app is judged' : 'How the fleet is judged'} meta={telemetry?.source === 'sim' ? 'simulated series' : telemetry?.window?.label}>
        {controls ? <div style={{ marginBottom: 12 }}><TelemetryControls controls={controls} /></div> : null}
        <dl className="live-rules">
          <div>
            <dt>Flag</dt>
            <dd>Robust z at least {T.z} for {T.consecutive} minutes in a row, and above the floor. A quiet app going from 1 to 4 requests a minute is not a spike.</dd>
          </div>
          <div>
            <dt>Baseline</dt>
            <dd>{hour ? 'Same-hour median over the earlier part of this window (the window spans 3 days or more).' : 'A single robust median. Hour-of-day baselines start once the window spans 3 days.'}</dd>
          </div>
          <div>
            <dt>Floors</dt>
            <dd>{T.floors.req} req/min · {(T.floors.s5xxRate * 100).toFixed(0)}% 5xx over at least {T.floors.s5xxMinReq} req/min · {T.floors.p95Ms} ms p95 · {T.floors.tokens.toLocaleString()} tokens.</dd>
          </div>
          <div>
            <dt>Incident</dt>
            <dd>z at least {T.severeZ}, or a 5xx rate at least {(T.severeS5xxRate * 100).toFixed(0)}%, opens an incident. Two linked apps flagging within {Math.round(T.cascadeMs / 60000)} minutes is a cascade.</dd>
          </div>
        </dl>
        <p className="live-key">
          <span><i className="is-measured" /> measured</span>
          <span><i className="is-base" /> baseline</span>
          <span><i className="is-floor" /> floor</span>
          <span><i className="is-flag" /> flagged</span>
        </p>
        {!slug && slugs.length > 1 && (
          <label className="cs-help" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            Series
            <select className="cs-input" style={{ width: 'auto' }} value={active} onChange={(e) => setPick(e.target.value)}>
              {slugs.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        )}
        {!series && <p className="cs-help">No traffic series yet. The edge access log fills this in once the host agent is on this pipeline.</p>}
        {series && (
          <>
            <SignalChart label="Requests / min" values={series.req} baseline={reqBase} floor={T.floors.req} flagIdx={flagIndexes(series.ts, step, anomalies, 'traffic')} format={n} />
            <SignalChart label="5xx rate" values={rate} baseline={rateBase} floor={T.floors.s5xxRate} flagIdx={flagIndexes(series.ts, step, anomalies, '5xx')} format={pct} hint={`Rate is counted only when the bucket has at least ${T.floors.s5xxMinReq} requests a minute.`} />
            <SignalChart label="p95 latency" values={series.p95} baseline={p95Base} floor={T.floors.p95Ms} flagIdx={flagIndexes(series.ts, step, anomalies, 'p95')} format={(v) => `${n(v)} ms`} />
            <SignalChart label="Tokens" values={series.tokens} baseline={tokBase} floor={T.floors.tokens} flagIdx={flagIndexes(series.ts, step, anomalies, 'usage')} format={n} />
          </>
        )}
        <p className="cs-help">{open ? `${open} open flag${open === 1 ? '' : 's'} in this window.` : 'Behavior baseline holding — nothing open in this window.'}</p>
      </LiveSection>
      <LiveSection title="Flags" meta={active || 'fleet'}>
        <AnomalyTable t={{ anomalies, thresholds: telemetry?.thresholds }} />
      </LiveSection>
    </>
  );
}
